if (!customElements.get('bundle-steps')) {
  customElements.define(
    'bundle-steps',
    class BundleSteps extends HTMLElement {
      connectedCallback() {
        this.STEP_DURATION     = 7000;
        this.VIEWPORT_DELAY   = 2000;
        this.TRANSITION_MS    = 400;

        this._activeIndex     = 0;
        this._hasStarted      = false;
        this._isTransitioning = false;
        this._stepTimer       = null;
        this._startDelayTimer = null;
        this._observer        = null;
        this._prefersReduced  = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

        this.stepBtns    = Array.from(this.querySelectorAll('.bundle-steps__step-btn'));
        this.slides      = Array.from(this.querySelectorAll('.bundle-steps__slide'));
        this.imageSlides = Array.from(this.querySelectorAll('.bundle-steps__image-slide'));

        if (!this.stepBtns.length || !this.slides.length) return;

        this._initRings();
        this._activateStep(0, { animate: false });
        this._bindEvents();

        // Mark ready in next frame so CSS transitions don't fire on load
        requestAnimationFrame(() => {
          this.classList.add('is-ready');
        });

        if (!this._prefersReduced) {
          this._setupObserver();
        }
      }

      disconnectedCallback() {
        this._clearTimers();
        if (this._observer) this._observer.disconnect();
        this.removeEventListener('click', this._handleClick);
        this.removeEventListener('keydown', this._handleKeydown);
      }

      // ---- Initialisation ----

      _initRings() {
        this.stepBtns.forEach((btn) => {
          const ring = btn.querySelector('.ring--progress');
          if (!ring) return;
          const circumference = 2 * Math.PI * parseFloat(ring.getAttribute('r'));
          ring.style.strokeDasharray  = circumference;
          ring.style.strokeDashoffset = circumference; // fully hidden at start
          btn._ringCircumference = circumference;
        });
      }

      _bindEvents() {
        this._handleClick   = this._handleClick.bind(this);
        this._handleKeydown = this._handleKeydown.bind(this);
        this.addEventListener('click', this._handleClick);
        this.addEventListener('keydown', this._handleKeydown);
      }

      _setupObserver() {
        this._observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting && !this._hasStarted) {
                this._hasStarted = true;
                this._observer.disconnect();
                this._startDelayTimer = setTimeout(() => {
                  this._startRingAnimation(this.stepBtns[this._activeIndex]);
                  this._scheduleNext();
                }, this.VIEWPORT_DELAY);
              }
            });
          },
          { threshold: 0.3 }
        );
        this._observer.observe(this);
      }

      // ---- Step activation ----

      _activateStep(newIndex, { animate = true, direction = 'forward' } = {}) {
        const prevIndex = this._activeIndex;

        if (animate && newIndex === prevIndex) return;
        if (animate && this._isTransitioning) return;
        if (animate) this._isTransitioning = true;

        const currentSlide      = this.slides[prevIndex];
        const nextSlide         = this.slides[newIndex];
        const currentImageSlide = this.imageSlides[prevIndex];
        const nextImageSlide    = this.imageSlides[newIndex];

        this._activeIndex = newIndex;

        // Update ARIA on step buttons
        this.stepBtns.forEach((btn, i) => {
          const isActive = i === newIndex;
          btn.classList.toggle('is-active', isActive);
          btn.setAttribute('aria-selected', isActive ? 'true' : 'false');
          btn.setAttribute('tabindex', isActive ? '0' : '-1');
        });

        if (!animate || this._prefersReduced) {
          // Instant swap — no slide animation
          this._finalizeActiveState(newIndex);
          return;
        }

        this._clearMotionState(this.slides);
        this._clearMotionState(this.imageSlides);

        // Ensure both outgoing and incoming items are visible during transition
        currentSlide.hidden = false;
        nextSlide.hidden = false;
        currentImageSlide.hidden = false;
        nextImageSlide.hidden = false;

        // Determine enter/exit class names from direction
        const exitClass  = direction === 'forward' ? 'is-exiting-left'  : 'is-exiting-right';
        const enterClass = direction === 'forward' ? 'is-entering-right' : 'is-entering-left';

        // 1. Position incoming slides off-screen (no transition)
        nextSlide.classList.add(enterClass);
        nextImageSlide.classList.add(enterClass);

        // 2. Force reflow so the browser registers the off-screen position
        void nextSlide.offsetWidth;

        // 3. In next frame: start all transitions
        requestAnimationFrame(() => {
          // Old slides exit
          currentSlide.classList.remove('is-active');
          currentSlide.classList.add(exitClass);
          currentImageSlide.classList.remove('is-active');
          currentImageSlide.classList.add(exitClass);

          // New slides enter
          nextSlide.classList.remove(enterClass);
          nextSlide.classList.add('is-active');
          nextImageSlide.classList.remove(enterClass);
          nextImageSlide.classList.add('is-active');

          // 4. Clean up after transition completes
          setTimeout(() => {
            this._finalizeActiveState(newIndex);
          }, this.TRANSITION_MS);
        });
      }

      _clearMotionState(collection) {
        collection.forEach((item) => {
          item.classList.remove('is-entering-left', 'is-entering-right', 'is-exiting-left', 'is-exiting-right');
        });
      }

      _finalizeActiveState(activeIndex) {
        this.slides.forEach((slide, index) => {
          const isActive = index === activeIndex;
          if (!isActive) {
            // Hide first, then reset classes to avoid a visible jump back to default transform.
            slide.hidden = true;
          }

          slide.classList.toggle('is-active', isActive);
          slide.classList.remove('is-entering-left', 'is-entering-right', 'is-exiting-left', 'is-exiting-right');

          if (isActive) {
            slide.hidden = false;
          }
        });

        this.imageSlides.forEach((imageSlide, index) => {
          const isActive = index === activeIndex;
          imageSlide.classList.toggle('is-active', isActive);
          imageSlide.classList.remove('is-entering-left', 'is-entering-right', 'is-exiting-left', 'is-exiting-right');
          imageSlide.setAttribute('aria-hidden', isActive ? 'false' : 'true');
        });

        this._isTransitioning = false;
      }

      // ---- Auto-rotation ----

      _scheduleNext() {
        this._clearTimers();
        this._stepTimer = setTimeout(() => {
          const nextIndex = (this._activeIndex + 1) % this.stepBtns.length;
          // Wrap: step N → step 0 is always treated as forward
          const direction = 'forward';
          this._stopRingAnimation(this.stepBtns[this._activeIndex]);
          this._activateStep(nextIndex, { animate: true, direction });
          this._startRingAnimation(this.stepBtns[nextIndex]);
          this._scheduleNext();
        }, this.STEP_DURATION);
      }

      _clearTimers() {
        if (this._stepTimer)       clearTimeout(this._stepTimer);
        if (this._startDelayTimer) clearTimeout(this._startDelayTimer);
        this._stepTimer       = null;
        this._startDelayTimer = null;
      }

      // ---- Ring animation ----

      _startRingAnimation(btn) {
        if (!btn || this._prefersReduced) return;
        const ring = btn.querySelector('.ring--progress');
        if (!ring) return;

        // Reset without transition
        ring.style.transition     = 'none';
        ring.style.strokeDashoffset = btn._ringCircumference;

        // Force reflow then start
        void ring.getBoundingClientRect();
        ring.style.transition     = `stroke-dashoffset ${this.STEP_DURATION}ms linear`;
        ring.style.strokeDashoffset = 0;
      }

      _stopRingAnimation(btn) {
        if (!btn) return;
        const ring = btn.querySelector('.ring--progress');
        if (!ring) return;
        ring.style.transition       = 'none';
        ring.style.strokeDashoffset = btn._ringCircumference;
      }

      // ---- Event handlers ----

      _handleClick(event) {
        const btn = event.target.closest('.bundle-steps__step-btn');
        if (!btn || !this.contains(btn)) return;

        const newIndex = this.stepBtns.indexOf(btn);
        if (newIndex === -1 || newIndex === this._activeIndex) return;

        const direction = newIndex > this._activeIndex ? 'forward' : 'back';

        // Cancel current ring + timer
        this._clearTimers();
        this._stopRingAnimation(this.stepBtns[this._activeIndex]);

        this._activateStep(newIndex, { animate: true, direction });

        // Restart auto-rotation from new step
        if (!this._prefersReduced && this._hasStarted) {
          this._startRingAnimation(this.stepBtns[newIndex]);
          this._scheduleNext();
        }
      }

      _handleKeydown(event) {
        const btn = event.target.closest('.bundle-steps__step-btn');
        if (!btn) return;

        const currentIndex = this.stepBtns.indexOf(btn);
        let nextIndex = null;

        switch (event.key) {
          case 'ArrowRight':
          case 'ArrowDown':
            event.preventDefault();
            nextIndex = (currentIndex + 1) % this.stepBtns.length;
            break;
          case 'ArrowLeft':
          case 'ArrowUp':
            event.preventDefault();
            nextIndex = (currentIndex - 1 + this.stepBtns.length) % this.stepBtns.length;
            break;
          case 'Home':
            event.preventDefault();
            nextIndex = 0;
            break;
          case 'End':
            event.preventDefault();
            nextIndex = this.stepBtns.length - 1;
            break;
          default:
            return;
        }

        if (nextIndex === null || nextIndex === currentIndex) return;

        const direction = nextIndex > currentIndex ? 'forward' : 'back';

        this._clearTimers();
        this._stopRingAnimation(this.stepBtns[this._activeIndex]);
        this._activateStep(nextIndex, { animate: true, direction });
        this.stepBtns[nextIndex].focus();

        if (!this._prefersReduced && this._hasStarted) {
          this._startRingAnimation(this.stepBtns[nextIndex]);
          this._scheduleNext();
        }
      }
    }
  );
}
